import React, { createContext, useLayoutEffect, useMemo } from 'react';
import type { BrandingContextValue } from './types';
import { resolveTenant } from './resolver';
import { resolveBranding } from './registry';
import { applyCssVars, applyDocumentMeta } from './dom';

export const BrandingContext = createContext<BrandingContextValue | null>(null);

export interface BrandingProviderProps {
  /**
   * Force un tenant (bypass la résolution par sous-domaine). Utile pour un build
   * mono-tenant natif, ou pour les tests.
   */
  tenantId?: string;
  /** Tenant de repli si la résolution runtime échoue (ex. `"sauf-imprevu"`). */
  fallbackTenant?: string;
  /**
   * Applique les `cssVars` du branding sur `:root`. Défaut `true`.
   * Passer `false` pour déléguer le thème à un provider existant (ex. Bocuse
   * conserve le `ThemeProvider` de core-ui). Note : un branding avec `cssVars`
   * vide `{}` est déjà un no-op même à `true`.
   */
  applyTheme?: boolean;
  /** Synchronise `document.title` / méta PWA. Défaut `true`. */
  setDocumentMeta?: boolean;
  children: React.ReactNode;
}

/**
 * Provider racine du branding multi-tenant.
 *
 * Résout le tenant courant (sous-domaine → env → fallback), charge son
 * enregistrement branding (cascade `parentTenantId` incluse), applique les
 * variables CSS + méta document, et expose le tout via `useBranding()`.
 *
 * L'application des CSS vars se fait en `useLayoutEffect` (avant peinture) ; le
 * `global.css` de chaque app fournit déjà les mêmes valeurs en fallback, donc
 * aucun flash pour le tenant hôte.
 */
export function BrandingProvider({
  tenantId,
  fallbackTenant,
  applyTheme = true,
  setDocumentMeta = true,
  children,
}: BrandingProviderProps) {
  const value = useMemo<BrandingContextValue>(() => {
    const env =
      typeof import.meta !== 'undefined'
        ? ((import.meta as unknown as { env?: Record<string, unknown> }).env)
        : undefined;
    const id =
      tenantId ??
      resolveTenant({
        hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
        env,
        fallback: fallbackTenant,
      });
    const branding = resolveBranding(id, fallbackTenant ?? undefined);
    return { tenantId: branding.id, branding, features: branding.features ?? {} };
  }, [tenantId, fallbackTenant]);

  useLayoutEffect(() => {
    if (applyTheme) applyCssVars(value.branding.cssVars ?? {});
    if (setDocumentMeta) applyDocumentMeta(value.branding);
  }, [value, applyTheme, setDocumentMeta]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
